/**
 * Which screens and sub-features a person has (owner, 2026-10-20: per-user access
 * by module and by report). The server enforces every one of these on its
 * endpoints (bible §38). This file only decides what is worth showing.
 *
 * A session cached before the module switches existed carries no
 * `permissions_version`. Until the server's /auth/me answer replaces it (or
 * permanently, offline) the old rules decide: the device switches if present,
 * otherwise the role list. So a pad is never shown "no screen" mid-service.
 */

// Screen → the switch that opens it. "report.*" = any one report.
export const SCREEN_PERMISSION = {
  billing: "pages.billing",
  captain: "pages.captain",
  kds: "pages.kds",
  inventory: "inventory.view",
  purchase: "purchase.view",
  approvals: "approvals.approve",
  register: "register.view",
  "staff/attendance": "attendance.manage",
  attendance: "attendance.manage",
  crm: "crm.view",
  reports: "report.*",
  dashboard: "pages.owner_dashboard",
};

// The 2026-10-18 device switches, honoured for a session from before 2026-10-20.
const V1_PERMISSION = { billing: "pages.billing", captain: "pages.captain", reports: "reports.view" };

export const knowsModules = (user, permissions) =>
  Array.isArray(permissions) && Number(user?.permissions_version || 0) >= 2;

export const hasAnyReport = permissions =>
  Array.isArray(permissions) && permissions.some(key => key.startsWith("report."));

const holds = (permissions, key) => (key === "report.*" ? hasAnyReport(permissions) : permissions.includes(key));

/**
 * May this person open `page`? `roles` is the page's pre-switch role list.
 */
export function screenAllowed(page, user, permissions, roles = []) {
  const key = SCREEN_PERMISSION[page];
  if (key && knowsModules(user, permissions)) return holds(permissions, key);
  const v1 = V1_PERMISSION[page];
  const knowsDevices = Array.isArray(permissions)
    && Object.values(V1_PERMISSION).some(k => permissions.includes(k));
  if (v1 && knowsDevices) return permissions.includes(v1);
  return roles.includes(user?.role);
}

/**
 * A sub-feature switch (e.g. "orders.pending_bills", "inventory.edit"). For an old
 * session it is `fallback`, the behaviour before the switch existed.
 */
export function featureAllowed(key, user, permissions, fallback = true) {
  if (!knowsModules(user, permissions)) return fallback;
  return permissions.includes(key);
}
